import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Manages optional add-on products bundled with the main product form.
 *
 * @extends Component
 */
class ProductAddonComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#updateAllItems();
    this.#setupBuyNowInterceptor();

    const { signal } = (this.#abortController = new AbortController());
    this.addEventListener('change', this.#handleChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Returns all checked add-on line items.
   * @returns {Array<{ variantId: string, quantity: number }>}
   */
  getAddonItems() {
    /** @type {Array<{ variantId: string, quantity: number }>} */
    const items = [];

    this.querySelectorAll('.product-addon__item').forEach((itemEl) => {
      const checkbox = itemEl.querySelector('[data-addon-checkbox]');
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;

      const variant = this.#getSelectedVariant(itemEl);
      if (!variant?.available || !variant.id) return;

      items.push({
        variantId: String(variant.id),
        quantity: 1,
      });
    });

    return items;
  }

  /**
   * @deprecated Use getAddonItems instead.
   * @returns {{ variantId: string, quantity: number } | null}
   */
  getAddonItem() {
    const items = this.getAddonItems();
    return items[0] ?? null;
  }

  /**
   * @param {Element} itemEl
   * @returns {Array<Record<string, unknown>>}
   */
  #parseVariants(itemEl) {
    const script = itemEl.querySelector('script[data-addon-variants]');
    const json = script?.textContent?.trim();
    if (!json) return [];

    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {Element} itemEl
   * @returns {Record<string, unknown> | undefined}
   */
  #getSelectedVariant(itemEl) {
    const variants = this.#parseVariants(itemEl);
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(itemEl.querySelectorAll('[data-addon-select]')));

    if (selects.length === 0) {
      return variants.find((variant) => variant.available) || variants[0];
    }

    const selectedOptions = selects.map((select) => select.value);

    return variants.find((variant) => {
      const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      if (optionValues.length < selectedOptions.length) return false;

      return selectedOptions.every((value, index) => optionValues[index] === value);
    });
  }

  #handleChange = () => {
    this.#updateAllItems();
  };

  #updateAllItems() {
    this.querySelectorAll('.product-addon__item').forEach((itemEl) => {
      this.#updateItemState(itemEl);
    });
  }

  /**
   * @param {Element} itemEl
   */
  #updateItemState(itemEl) {
    const variant = this.#getSelectedVariant(itemEl);
    const price = itemEl.querySelector('[data-addon-price]');

    if (price && typeof variant?.price === 'number') {
      const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      const currency = this.dataset.currency || 'USD';
      price.textContent = formatMoney(variant.price, moneyFormat, currency);
    }

    const image = itemEl.querySelector('[data-addon-image]');
    const featuredImage = /** @type {{ src?: string, alt?: string } | undefined} */ (variant?.featured_image);

    if (image instanceof HTMLImageElement && featuredImage?.src) {
      image.src = `${featuredImage.src}&width=120`;
      if (featuredImage.alt) {
        image.alt = featuredImage.alt;
      }
    }
  }

  #getProductForm() {
    const section = this.closest('.shopify-section, featured-product-information');
    return /** @type {HTMLElement | null} */ (section?.querySelector('product-form-component'));
  }

  #setupBuyNowInterceptor() {
    if (this.closest('featured-product-information')) return;

    const productForm = this.#getProductForm();
    const checkoutContainer = productForm?.querySelector('.accelerated-checkout-block');
    if (!checkoutContainer) return;

    checkoutContainer.addEventListener('click', this.#handleBuyNowClick, {
      capture: true,
      signal: this.#abortController?.signal,
    });

    const observer = new MutationObserver(() => {
      const button = checkoutContainer.querySelector('shopify-accelerated-checkout, .shopify-payment-button');
      if (!button) return;
      button.addEventListener('click', this.#handleBuyNowClick, {
        capture: true,
        signal: this.#abortController?.signal,
      });
    });

    observer.observe(checkoutContainer, { childList: true, subtree: true });
    this.#abortController?.signal.addEventListener('abort', () => observer.disconnect());
  }

  /** @param {Event} event */
  #handleBuyNowClick = async (event) => {
    const addonItems = this.getAddonItems();
    if (addonItems.length === 0) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('shopify-accelerated-checkout, .shopify-payment-button__button, more-payment-options-link')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const productForm = this.#getProductForm();
    const variantInput = productForm?.querySelector('input[name="id"]');
    const quantityInput = productForm?.querySelector('quantity-selector-component input[ref="quantityInput"]');
    const mainVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
    const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;

    if (!mainVariantId) return;

    const payload = {
      items: [
        { id: Number(mainVariantId), quantity },
        ...addonItems.map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
        })),
      ],
    };

    try {
      const clearResponse = await fetch(`${Theme.routes.cart_url}/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!clearResponse.ok) return;

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status) return;

      window.location.href = '/checkout';
    } catch (error) {
      console.error('[product-addon] Buy now failed:', error);
    }
  };
}

if (!customElements.get('product-addon-component')) {
  customElements.define('product-addon-component', ProductAddonComponent);
}
